import {
  Avatar,
  Box,
  Button,
  Flex,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { nameInitials } from "../../../utils/nameInitials";
import { Edit, InfoCircle, TickCircle } from "iconsax-react";
import { TagCapsules, TagStatus } from "../../../components";
import { useDisclosure } from "@mantine/hooks";
import ModalEditProfile from "./ModalEditProfile";
import {
  convertCep,
  convertCpf,
  convertPhone,
} from "../../../utils/convertDataUser";
import { IColor } from "../../../utils/selectColors";
import { useUser } from "../../../hooks/useUsers";
import { client_selected } from "../../../client";
import { useTranslation } from "react-i18next";

const ProfileInformation = () => {
  const theme = useMantineTheme();
  const { t } = useTranslation();
  const isEmpiricus = client_selected.name === "Empiricus";
  const [opened, { open, close }] = useDisclosure(false);
  const { data } = useUser({ enable: true });

  const user = [
    {
      name: t("meu_perfil.configuracoes.user.name"),
      data: data?.display_name || "----",
      showField: true,
    },
    {
      name: !isEmpiricus
        ? t("meu_perfil.configuracoes.user.cpf")
        : t("meu_perfil.configuracoes.user.doc"),
      data: !isEmpiricus
        ? convertCpf(data?.document_nr || "----")
        : data?.document_nr || "----",
      showField: true,
    },
    {
      name: t("meu_perfil.configuracoes.user.email"),
      data: data?.email || "----",
      showField: true,
    },
    {
      name: t("meu_perfil.configuracoes.user.phone"),
      data: convertPhone(data?.cell_phone || "----"),
      showField: !isEmpiricus,
    },
    {
      name: t("meu_perfil.configuracoes.user.whatsapp"),
      data: convertPhone(data?.cell_phone || "----"),
      showField: !isEmpiricus,
    },
  ] as const;

  const adress = [
    {
      name: t("meu_perfil.configuracoes.address.zip_code"),
      data: convertCep(data?.address?.cep || "----"),
    },
    {
      name: t("meu_perfil.configuracoes.address.address"),
      data: data?.address?.street
        ? `${data?.address?.street}, ${data?.address?.number || ""}`
        : "----",
    },
    {
      name: t("meu_perfil.configuracoes.address.neighborhood"),
      data: data?.address?.neighborhood || "----",
    },
    {
      name: t("meu_perfil.configuracoes.address.additional_address"),
      data: data?.address?.additional_address || "----",
    },
    {
      name: t("meu_perfil.configuracoes.address.state_city"),
      data: data?.address?.state
        ? `${data?.address?.state}/${data?.address?.city || ""}`
        : "----",
    },
  ] as const;

  // const notify = [
  //   {
  //     name: t("meu_perfil.notifications.email_new_order_sent"),
  //     data: data?.notifications?.email_new_order_sent || false,
  //   },
  //   {
  //     name: t("meu_perfil.notifications.email_scheduled_user_plan_action"),
  //     data: data?.notifications?.email_scheduled_user_plan_action || false,
  //   },
  //   {
  //     name: t("meu_perfil.notifications.email_executed_user_plan_action"),
  //     data: data?.notifications?.email_executed_user_plan_action || false,
  //   },
  // ] as const;

  return (
    <>
      {!isEmpiricus && opened ? (
        <ModalEditProfile opened={opened} close={close} />
      ) : null}
      <Flex
        direction="column"
        justify="start"
        align="start"
        w="100%"
        h={"100%"}
        gap={7}
      >
        <Flex
          direction="column"
          bg={"basic.0"}
          p={{ base: 16, 991: 24, 1400: 48 }}
          w="100%"
          sx={{
            borderRadius: "12px",
            boxShadow: "0px 25px 35px rgba(0, 0, 0, 0.03)",
          }}
        >
          <Flex
            direction={{ base: "column-reverse", 1400: "row" }}
            gap={{ base: 24 }}
            align={{ base: "start", 991: "center" }}
            w="100%"
          >
            <Flex
              gap={{ base: 24 }}
              w="100%"
              align={{ base: "start", 575: "center", 991: "center" }}
              direction={{ base: "column", 575: "row", 991: "row" }}
            >
              <Avatar
                radius="md"
                size={80}
                src={null}
                alt="avatar"
                color={theme.colors.primary[0]}
              >
                <Text
                  fw={300}
                  h={80}
                  miw={80}
                  bg="primary.1"
                  c="basic.0"
                  align="center"
                  pt={16}
                >
                  {nameInitials(data?.name)}
                </Text>
              </Avatar>
              <Flex direction={"column"} gap="16px">
                <Text
                  tt="uppercase"
                  fw={{ base: 700 }}
                  fz={{ base: 32 }}
                  lh={{ base: "125%" }}
                  c="basic.5"
                  style={{ wordBreak: "break-word" }}
                >
                  {data?.name}
                </Text>
                <TagStatus
                  miw={65}
                  title={
                    data?.status
                      ? t("meu_perfil.configuracoes.status_active")
                      : t("meu_perfil.configuracoes.status_inactive")
                  }
                  border={true}
                  status={data?.status ? IColor.success : IColor.disapproved}
                />
              </Flex>
            </Flex>
            {!isEmpiricus ? (
              <Button
                onClick={open}
                fullWidth
                maw={{ base: "100%", 991: 260 }}
                variant="primary"
                ml={{ base: "unset", 991: "auto" }}
                leftIcon={<Edit size="24" />}
              >
                {t("meu_perfil.configuracoes.button_edit")}
              </Button>
            ) : null}
          </Flex>

          <Flex
            justify={"space-between"}
            mt={{ base: 24, 991: 48 }}
            gap={{ base: 16, 991: 32 }}
            wrap="wrap"
          >
            {user.map((x, index) => {
              return x.showField ? (
                <Box key={x.name}>
                  <Text fz={{ base: 14 }} lh={{ base: "155%" }} c="basic.5">
                    {x.name}
                  </Text>

                  <Text
                    truncate
                    mt={4}
                    fw={{ base: 600 }}
                    fz={{ base: 14 }}
                    lh={{ base: "155%" }}
                    c="basic.5"
                    style={{ wordBreak: "break-word", whiteSpace: "wrap" }}
                  >
                    {x.data === null ? "---" : x.data}
                  </Text>
                </Box>
              ) : null;
            })}
          </Flex>
        </Flex>
        {client_selected.hasBrokers ? (
          <Flex
            id="editDataProfile"
            direction="column"
            bg={"basic.0"}
            p={{ base: 48 }}
            w="100%"
            sx={{
              borderRadius: "12px",
              boxShadow: "0px 25px 35px rgba(0, 0, 0, 0.03)",
            }}
          >
            <Text
              mb={{ base: 24 }}
              fw={{ base: 600 }}
              fz={{ base: 16 }}
              lh={{ base: "155%" }}
              c="basic.5"
            >
              {t("meu_perfil.configuracoes.brokers")}
            </Text>

            {!!data?.secondaryEmails && data?.secondaryEmails?.length > 0 ? (
              <Flex gap={{ base: 32 }} wrap="wrap">
                {data?.secondaryEmails.map((x, index: number) => (
                  <Box key={`${x.email}-${index}`}>
                    <Text
                      fz={{ base: 14 }}
                      fw={{ base: 400 }}
                      lh={{ base: "155%" }}
                      c={"basic.5"}
                    >
                      {`E-mail ${index + 1}`}
                    </Text>
                    <Text
                      fz={{ base: 14 }}
                      fw={{ base: 600 }}
                      lh={{ base: "21.7px" }}
                      c={"basic.5"}
                    >
                      {x.email}
                    </Text>
                    <Flex>
                      {x.is_active ? (
                        <Flex gap={6} mt={2}>
                          <TickCircle
                            size="24"
                            color={theme.colors.complementary[0]}
                            variant="Bold"
                          />
                          <TagCapsules
                            radius={4}
                            border={true}
                            colorTag={IColor.green}
                          >
                            {t("meu_perfil.configuracoes.brokers_validated")}
                          </TagCapsules>
                        </Flex>
                      ) : (
                        <Flex gap={6} mt={2}>
                          <InfoCircle
                            size="24"
                            color={theme.colors.alerts[1]}
                            variant="Bold"
                          />
                          <TagCapsules
                            radius={4}
                            border={true}
                            colorTag={IColor.orange}
                          >
                            {t("meu_perfil.configuracoes.brokers_pending")}
                          </TagCapsules>
                        </Flex>
                      )}
                    </Flex>
                  </Box>
                ))}
              </Flex>
            ) : (
              t("meu_perfil.configuracoes.no_emails")
            )}
          </Flex>
        ) : null}
        {!isEmpiricus ? (
          <Flex
            id="editDataProfile"
            direction="column"
            bg={"basic.0"}
            p={{ base: 16, 991: 24, 1400: 48 }}
            w="100%"
            sx={{
              borderRadius: "12px",
              boxShadow: "0px 25px 35px rgba(0, 0, 0, 0.03)",
            }}
          >
            <Text
              mb={{ base: 24 }}
              fw={{ base: 600 }}
              fz={{ base: 16 }}
              lh={{ base: "155%" }}
              c="basic.5"
            >
              {t("meu_perfil.configuracoes.address.title")}
            </Text>
            <Flex gap={{ base: 32 }} wrap="wrap" justify={"space-between"}>
              {adress.map((x: any) => (
                <Box key={x.name}>
                  <Text fz={{ base: 14 }} lh={{ base: "155%" }} c="basic.5">
                    {x.name}
                  </Text>
                  <Text
                    mt={4}
                    fw={{ base: 600 }}
                    fz={{ base: 14 }}
                    lh={{ base: "155%" }}
                    c="basic.5"
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {x.data === null ? "---" : x.data}
                  </Text>
                </Box>
              ))}
            </Flex>
          </Flex>
        ) : null}
        {/* <Flex
          mb={108}
          id="editDataProfile"
          direction="column"
          bg={"basic.0"}
          p={{ base: 48 }}
          w="100%"
          sx={{
            borderRadius: "12px",
            boxShadow: "0px 25px 35px rgba(0, 0, 0, 0.03)",
          }}
        >
          <Text
            mb={{ base: 24 }}
            fw={{ base: 600 }}
            fz={{ base: 16 }}
            lh={{ base: "155%" }}
            c="basic.5"
          >
            {t("meu_perfil.configuracoes.notifications.title")}
          </Text>
          <Flex gap={{ base: 32 }} wrap="wrap" justify={"start"}>
            {notify.map((x) => (
              <Flex key={x.name} direction={"column"} gap="8px">
                <Text fz={{ base: 14 }} lh={{ base: "155%" }} c="basic.5">
                  {x.name}
                </Text>
                <TagStatus
                  border
                  title={x.data ? t("meu_perfil.configuracoes.status_active")
                      : t("meu_perfil.configuracoes.status_inactive")}
                  status={x.data ? IColor.success : IColor.disapproved}
                />
              </Flex>
            ))}
          </Flex>
        </Flex> */}
      </Flex>
    </>
  );
};

export default ProfileInformation;
